import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback } from 'react';

import ItemCardTitle from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getBidderId } from '@/redux/modules/account/user/user.selectors';
import {
    getRegistrationDeclined,
    getRegistrationPending,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getLiveItemDataLeadingBidder } from '@/redux/modules/console';
import {
    getItemSummaryCatalogId,
    getItemSummaryCatalogOnly,
    getItemSummaryCatalogStatus,
    getItemSummaryIsLocked,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummarySellerId,
    getItemSummarySellerName,
    getItemSummaryStartTime,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { openSendSellerMessageModal } from '@/redux/modules/modal';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { shouldShowLive } from '@/utils/live';

import { CardBidding } from '../CardBidding/CardBidding';
import { CardInfo } from '../CardInfo/CardInfo';
import { CardPrice } from '../CardPrice/CardPrice';
import { CardRegistration } from '../CardRegistration/CardRegistration';

export type CardGridBodyProps = {
    cardHovered: boolean;
    enterLinkHoverState: () => void;
    isCurrentLiveitem: boolean;
    isMobile: boolean;
    isStoppedFromBidding: boolean;
    itemId: number;
    leaveLinkHoverState: () => void;
    linkHovered: boolean;
};

// Literal class lookups (no bracket-value interpolation) so Tailwind's content scanner can find them.
const GRID_TEMPLATE_ROWS_CLASS = {
    denied: {
        short: '[grid-template-rows:8px_min-content_4px_min-content_4px_min-content_16px_min-content_4px_min-content]',
        tall: '[grid-template-rows:8px_min-content_4px_48px_4px_min-content_16px_min-content_4px_min-content]',
    },
    open: {
        short: '[grid-template-rows:8px_min-content_4px_min-content_4px_min-content_16px_min-content]',
        tall: '[grid-template-rows:8px_min-content_4px_48px_4px_min-content_16px_min-content]',
    },
};

const GRID_TEMPLATE_AREAS_CLASS = {
    denied: "[grid-template-areas:'.'_'iteminfo'_'.'_'title'_'.'_'currentbidprice'_'.'_'registerForAuction'_'.'_'messageAuctioneerLink']",
    open: "[grid-template-areas:'.'_'iteminfo'_'.'_'title'_'.'_'currentbidprice'_'.'_'registerForAuction'_'.'_'bidButtonForLiveItem']",
};

export const CardGridBody = ({
    cardHovered,
    enterLinkHoverState,
    isCurrentLiveitem,
    isMobile,
    isStoppedFromBidding,
    itemId,
    leaveLinkHoverState,
    linkHovered,
}: CardGridBodyProps) => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();

    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const isLocked = useAppSelector((state) => getItemSummaryIsLocked(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryCatalogOnly(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummaryStartTime(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemSummarySellerName(state, itemId));
    const declined = useAppSelector((state) => getRegistrationDeclined(state, catalogId));
    const pending = useAppSelector((state) => getRegistrationPending(state, catalogId));

    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const bidderId = useAppSelector(getBidderId);
    const leadingBidderId = useAppSelector((state) => getLiveItemDataLeadingBidder(state, itemId));

    const leadingBidder = leadingBidderId === bidderId;

    const showLive = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);

    const isDone = catalogStatus === 'done';
    const isLive = catalogStatus === 'live';

    const isPending = isLocked && !isPassed && !isSold && !isLotOpen;
    const shouldIncreaseTitleContainerHeight = title.length < 30;
    const showLiveBidButton = showLive && !isStoppedFromBidding && !leadingBidder && isCurrentLiveitem;
    const showDeniedRegistration = declined && !isMobile && !isDone && !showLiveBidButton;
    const catalogStatusIsNotDoneOrLive = !isLive && !isDone;

    const showSendSellerMessageModalDeclined = useCallback(() => {
        const messageDetails = {
            catalogId,
            message: `${formatMessage(['message_registration_declined'])}`,
            title,
        };

        const subject = `${formatMessage(['registration_declined'])}: ${title}`;

        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [catalogId, dispatch, formatMessage, sellerName, sellerId, title]);

    // Cascade precedence from the original styled-component (later block wins when its condition
    // is true): tall-title, then pending (forces short, regardless of tall), then denied /
    // denied+tall (which override pending's effect, but still respect tall-title). So pending
    // only forces "short" when not denied.
    const forcedShortByPending = pending && !showDeniedRegistration;
    const rowsHeight = !forcedShortByPending && shouldIncreaseTitleContainerHeight ? 'tall' : 'short';
    const gridState = showDeniedRegistration ? 'denied' : 'open';

    return (
        <section
            className={cn(
                'grid overflow-hidden [grid-area:alldetailsformobilelayout]',
                GRID_TEMPLATE_AREAS_CLASS[gridState],
                GRID_TEMPLATE_ROWS_CLASS[gridState][rowsHeight],
                "xsMax:[grid-template-areas:'iteminfo'_'.'_'title'_'.'_'currentbidprice'_'.'_'registerForAuction'_'.'_'bidButtonForLiveItem']",
                'xsMax:[grid-template-rows:17px_6px_min-content_2px_20px_4px_min-content_4px]',
                'max-[560px]:[grid-template-rows:22px_0_min-content_4px_min-content]'
            )}
            data-testid="placed-details-for-mobile"
        >
            <CardInfo
                isCatalogOnline={catalogStatus === 'online'}
                isLive={isLive}
                isMobile={isMobile}
                isPending={isPending}
                itemId={itemId}
            />
            <div className="[grid-area:title]">
                <ItemCardTitle
                    isHovered={linkHovered}
                    itemId={itemId}
                    onMouseEnter={enterLinkHoverState}
                    onMouseLeave={leaveLinkHoverState}
                    showLotNumber
                    testid="itemCardTitle"
                />
            </div>
            <CardPrice
                cardHovered={cardHovered}
                enterLinkHoverState={enterLinkHoverState}
                isMobile={isMobile}
                isPending={isPending}
                itemId={itemId}
                leaveLinkHoverState={leaveLinkHoverState}
                linkHovered={linkHovered}
            />
            {catalogStatusIsNotDoneOrLive && !showLiveBidButton && (
                <CardRegistration
                    isMobile={isMobile}
                    itemId={itemId}
                />
            )}
            {showDeniedRegistration && (
                <Link
                    className="no-underline [grid-area:messageAuctioneerLink] hover:underline"
                    onClick={showSendSellerMessageModalDeclined}
                >
                    {formatMessage(['registration', 'message_auctioneer'])}
                </Link>
            )}
            <CardBidding
                itemId={itemId}
                showLiveBidButton={showLiveBidButton}
            />
        </section>
    );
};
